/**
 * Os nomes das permissões, em português — e agrupados como se lêem.
 *
 * O editor de cargos mostrava o BIT: pegava em `MANAGE_ROLES`, punha em
 * minúsculas e trocava os `_` por espaços. Saía "Manage roles", "Read message
 * history", "Share screen" — trinta e três linhas em inglês no meio de uma app
 * inteira em português, e a única parte dela onde se decide quem pode o quê.
 * Uma pessoa que não saiba inglês não podia gerir o seu próprio servidor.
 *
 * Duas decisões que este ficheiro toma:
 *
 *  1. TRADUZ-SE O QUE A PERMISSÃO FAZ, não o nome dela. `MANAGE_PERMISSIONS`
 *     não é "gerir permissões" (isso seria `MANAGE_ROLES` visto de fora): é
 *     mexer nas excepções POR CANAL. E `CHANGE_NICKNAME` é a própria, enquanto
 *     `MANAGE_NICKNAMES` é a dos outros — a diferença some-se num "gerir
 *     alcunhas" e é exactamente a que faz alguém dar o poder errado.
 *  2. GRUPOS, e são os mesmos quatro que o `Permission` já tem escritos em
 *     comentários. Trinta e três caixas em fila são uma parede; em quatro
 *     blocos lê-se por assunto — "isto é de voz" — e escolhe-se sem ler tudo.
 *
 * O teste ao lado garante que cada bit tem nome: uma permissão nova sem entrada
 * aqui voltaria a aparecer em inglês, e ninguém repara nisso numa lista de 33.
 */

import type { PermissionName } from "@greenvoice/shared";

export const NOME_DA_PERMISSAO: Record<PermissionName, string> = {
  // ── Servidor ────────────────────────────────────────────────
  ADMINISTRATOR: "Administrador (tudo)",
  MANAGE_SERVER: "Gerir o servidor",
  MANAGE_CHANNELS: "Criar e editar canais",
  MANAGE_ROLES: "Gerir cargos",
  MANAGE_PERMISSIONS: "Gerir permissões de cada canal",
  MANAGE_EMOJIS: "Gerir emojis",
  VIEW_AUDIT_LOG: "Ver o registo de actividade",

  // ── Membros ─────────────────────────────────────────────────
  KICK_MEMBERS: "Expulsar membros",
  BAN_MEMBERS: "Banir membros",
  TIMEOUT_MEMBERS: "Suspender membros temporariamente",
  ASSIGN_ROLES: "Atribuir cargos a membros",
  CHANGE_NICKNAME: "Mudar a própria alcunha",
  MANAGE_NICKNAMES: "Mudar a alcunha dos outros",

  // ── Canais de texto ─────────────────────────────────────────
  VIEW_CHANNEL: "Ver o canal",
  READ_MESSAGE_HISTORY: "Ler as mensagens antigas",
  SEND_MESSAGES: "Enviar mensagens",
  MANAGE_MESSAGES: "Apagar e fixar mensagens de outros",
  ATTACH_FILES: "Anexar ficheiros",
  EMBED_LINKS: "Mostrar pré-visualização de links",
  ADD_REACTIONS: "Reagir com emojis",
  MENTION_EVERYONE: "Mencionar @todos",
  CREATE_INVITES: "Criar convites",
  MANAGE_WEBHOOKS: "Gerir webhooks",
  CREATE_THREADS: "Abrir tópicos",
  MANAGE_THREADS: "Gerir tópicos",

  // ── Voz, vídeo e ecrã ───────────────────────────────────────
  CONNECT: "Entrar nas salas de voz",
  SPEAK: "Falar",
  VIDEO: "Ligar a câmara",
  SHARE_SCREEN: "Partilhar o ecrã",
  PRIORITY_SPEAKER: "Voz prioritária",
  MUTE_MEMBERS: "Silenciar o microfone de outros",
  DEAFEN_MEMBERS: "Ensurdecer outros",
  MOVE_MEMBERS: "Mover membros entre salas",
  RECORD_SESSION: "Gravar a chamada",
};

export interface GrupoDePermissoes {
  titulo: string;
  permissoes: readonly PermissionName[];
}

export const GRUPOS_DE_PERMISSOES: readonly GrupoDePermissoes[] = [
  {
    titulo: "Servidor",
    permissoes: [
      "ADMINISTRATOR",
      "MANAGE_SERVER",
      "MANAGE_CHANNELS",
      "MANAGE_ROLES",
      "MANAGE_PERMISSIONS",
      "MANAGE_EMOJIS",
      "VIEW_AUDIT_LOG",
    ],
  },
  {
    titulo: "Membros",
    permissoes: [
      "KICK_MEMBERS",
      "BAN_MEMBERS",
      "TIMEOUT_MEMBERS",
      "ASSIGN_ROLES",
      "CHANGE_NICKNAME",
      "MANAGE_NICKNAMES",
    ],
  },
  {
    titulo: "Canais de texto",
    permissoes: [
      "VIEW_CHANNEL",
      "READ_MESSAGE_HISTORY",
      "SEND_MESSAGES",
      "MANAGE_MESSAGES",
      "ATTACH_FILES",
      "EMBED_LINKS",
      "ADD_REACTIONS",
      "MENTION_EVERYONE",
      "CREATE_INVITES",
      "MANAGE_WEBHOOKS",
      "CREATE_THREADS",
      "MANAGE_THREADS",
    ],
  },
  {
    titulo: "Voz, vídeo e ecrã",
    permissoes: [
      "CONNECT",
      "SPEAK",
      "VIDEO",
      "SHARE_SCREEN",
      "PRIORITY_SPEAKER",
      "MUTE_MEMBERS",
      "DEAFEN_MEMBERS",
      "MOVE_MEMBERS",
      "RECORD_SESSION",
    ],
  },
];
